import {useCallback, useMemo} from "react";
import {Box} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {TaxonomyChangeRow} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import {getYourChangesColumns} from "./YourChangesColumns";
import {buildChangeDiffGraph} from "./diff/taxonomyChangeDiffGraph";
import {ChangeDiffCanvas} from "./diff/ChangeDiffCanvas";

/** Every opened row is this tall; the picture zooms out to fit inside it. */
const DETAIL_PANEL_HEIGHT = 350;
/** Padding around the picture inside an opened row. */
const DETAIL_PADDING = 16;
// Stable, so the grid doesn't re-measure the opened rows on every render.
const getDetailPanelHeight = () => DETAIL_PANEL_HEIGHT;

export type YourChangesTableProps = {
    rows: TaxonomyChangeRow[];
    /** The taxonomy the changes are on, for the before / after picture */
    tree?: m_taxonomy.Tree;
    isLoading: boolean;
    title?: string;
    /** After the change columns, e.g. the approval page's notes and revert */
    extraColumns?: GridColDef<TaxonomyChangeRow>[];
    /** Where the column layout is remembered; one per page that shows the table */
    layoutKey?: string;
    noRowsLabel?: string;
};

/** One row per changed category; a row opens to a picture of its changes, before and after combined. */
export const YourChangesTable = ({
                                     rows,
                                     tree,
                                     isLoading,
                                     title,
                                     extraColumns,
                                     layoutKey = 'taxonomy-builder-v2-changes',
                                     noRowsLabel = 'No changes in your draft.',
                                 }: YourChangesTableProps) => {
    // One small picture per change, shown when its row is opened.
    const graphs = useMemo(
        () => new Map(tree ? rows.map(row => [row.id, buildChangeDiffGraph(tree, row)]) : []),
        [rows, tree],
    );
    const graphOf = useCallback((row: TaxonomyChangeRow) => graphs.get(row.id), [graphs]);

    return (
        <CustomDataGrid
            layoutKey={layoutKey}
            rows={rows}
            columns={extraColumns ? [...getYourChangesColumns(), ...extraColumns] : getYourChangesColumns()}
            loading={isLoading}
            showToolbar
            disableRowSelectionOnClick
            disableColumnSorting
            toolbarProps={{
                title: title,
                showFilterPanel: false,
            }}
            density="standard"
            autoRowHeight
            localeText={{noRowsLabel}}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={({row}) => {
                const graph = graphOf(row);
                return graph && (
                    <Box sx={{p: `${DETAIL_PADDING}px`}}>
                        <ChangeDiffCanvas nodes={graph.nodes} edges={graph.edges} height={DETAIL_PANEL_HEIGHT - DETAIL_PADDING * 2}/>
                    </Box>
                );
            }}
        />
    );
};
